import { useEffect, useState } from 'react'

import { useMatches } from '@tanstack/react-router'

// Imported through Vite so the files land in /assets/* with hashed names —
// the only path the CF Pages _headers file serves with immutable caching
// (everything under public/ is no-store, which re-downloaded the photo on
// every refresh and flashed a white panel).
import loginPhoto from '@/assets/auth/auth-login.webp'
import registerPhoto from '@/assets/auth/auth-register.webp'
import { SignaterRectangleLogo } from '@/components/common/signater-rectangle-logo'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useManropeFont } from '@/hooks/use-manrope-font'
import { cn } from '@/lib/utils'

import locales from './locales'

type PanelVariant = 'login' | 'register'

const PANEL_IMAGES: Record<PanelVariant, string> = {
  login: loginPhoto,
  register: registerPhoto,
}

type PhotoPanelProps = {
  className?: string
}

export function PhotoPanel({ className }: PhotoPanelProps) {
  const { t } = useComponentTranslation(locales)
  useManropeFont()
  const matches = useMatches()

  const variant: PanelVariant = matches.some(
    (match) => match.pathname === '/register',
  )
    ? 'register'
    : 'login'
  const [isLoaded, setIsLoaded] = useState(false)

  // Warm the other variant so switching login<->register never flashes.
  useEffect(() => {
    const other = new Image()
    other.src = PANEL_IMAGES[variant === 'login' ? 'register' : 'login']
  }, [variant])

  return (
    <aside
      className={cn(
        // Dark ground while the photo decodes: the wordmark and headline are
        // white, so the panel is fully readable even before the first byte.
        'relative overflow-hidden rounded-3xl bg-zinc-900',
        className,
      )}
    >
      <img
        src={PANEL_IMAGES[variant]}
        alt={t(`${variant}Alt`)}
        fetchPriority="high"
        decoding="async"
        onLoad={() => setIsLoaded(true)}
        className={cn(
          'absolute inset-0 h-full w-full object-cover transition-opacity duration-300',
          isLoaded ? 'opacity-100' : 'opacity-0',
        )}
      />
      {/* Scrims keep the white wordmark (top) and headline (bottom) readable
          over any photo, bright ones included. */}
      <div
        className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-black/50 to-transparent"
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 via-black/25 to-transparent"
        aria-hidden="true"
      />
      <div className="absolute top-8 left-8">
        <SignaterRectangleLogo forceTheme="dark" height={32} />
      </div>
      {/* Manrope (loaded by useManropeFont) mirrors signater.com's marketing
          type. A <p>, not a heading: the card's own heading is the page's
          functional heading and a marketing heading would outrank it in the
          document outline. */}
      <p
        className="absolute inset-x-8 bottom-8 text-4xl font-extrabold tracking-[-0.03em] text-balance text-white"
        style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
      >
        {t(`${variant}Headline`)}
      </p>
    </aside>
  )
}
